/**
 * Copyright (c) 2025-2026 eBUCIS, LLC. All rights reserved.
 *
 * AuthLayout Component - Split-screen layout for authentication pages
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import React from 'react'
import { motion } from 'framer-motion'
import PropTypes from 'prop-types'
import { useBranding } from '../../stores/brandingStore'
import DeaverisLogo from '../../assets/deaveris.svg'
import PatentPendingBadge from '../PatentPendingBadge'
import AppCopyright from '../AppCopyright'

// Memoize all framer-motion animation objects outside component
const MARKETING_ANIMATION = {
  initial: { opacity: 0, x: -50 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.6 }
}

const FORM_CONTAINER_ANIMATION = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay: 0.2 }
}

const FORM_TITLE_ANIMATION = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { delay: 0.3 }
}

const FORM_SUBTITLE_ANIMATION = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { delay: 0.4 }
}

const FORM_CONTENT_ANIMATION = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { delay: 0.5 }
}

const FOOTER_ANIMATION = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { delay: 0.6 }
}

/**
 * AuthLayout Component
 *
 * Modern split-screen layout for authentication pages
 * - Desktop: 50/50 split (marketing left, form right)
 * - Mobile: Simplified marketing header + full-width form below
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Form content to display
 * @param {React.ReactNode} props.marketingContent - Marketing panel content
 * @param {string} props.formTitle - Title for the form section
 * @param {string} props.formSubtitle - Subtitle for the form section
 */
const AuthLayout = ({ children, marketingContent, formTitle, formSubtitle }) => {
  const branding = useBranding()
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="flex min-h-screen">
        {/* Marketing Panel - Desktop Only (Left Side) */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
          {/* Decorative Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          </div>

          {/* Marketing Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="relative z-10 flex flex-col justify-center w-full p-12 text-white"
          >
            {marketingContent}
          </motion.div>
        </div>

        {/* Form Panel (Right Side) */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24">
          {/* Mobile Marketing Header */}
          <div className="lg:hidden mb-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
              <img src={DeaverisLogo} alt="Deaveris" className="w-full h-full rounded-2xl" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {branding.name.toUpperCase()}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{branding.tagline}</p>
          </div>

          {/* Form Container */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="w-full max-w-md mx-auto"
          >
            {/* Form Header */}
            <div className="mb-8">
              <motion.h2
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-3xl font-bold text-gray-900 dark:text-white mb-2"
              >
                {formTitle}
              </motion.h2>
              {formSubtitle && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-gray-600 dark:text-gray-400"
                >
                  {formSubtitle}
                </motion.p>
              )}
            </div>

            {/* Form Content */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {children}
            </motion.div>
          </motion.div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-8 flex flex-col items-center gap-2 text-center text-sm text-gray-500 dark:text-gray-400"
          >
            <PatentPendingBadge />
            <AppCopyright as="p" />
          </motion.div>
        </div>
      </div>
    </div>
  )
}

AuthLayout.propTypes = {
  children: PropTypes.node.isRequired,
  marketingContent: PropTypes.node.isRequired,
  formTitle: PropTypes.string.isRequired,
  formSubtitle: PropTypes.string
}

export default AuthLayout
